import api from '../../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiInbox } from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import { VscClearAll } from 'react-icons/vsc'

import Input from "../../../shared/components/form/input"
import useMyPermission from '../../../hooks/useMyPermission'
import ListServiceOrderRow from './ListRow/ListServiceOrderRow'
import Modal from '../../../shared/modal/Modal'
import NewServiceOrder from './NewServiceOrder'
import ServiceOrderFilterModal from './ServiceOrderFilterModal'
import Button from '../../../shared/components/Button/Button'
import './ServiceOrder.css'

const EMPTY_FILTERS = { status: 'all', client: null, technician: null, procedure: null, dateFrom: '', dateTo: '' }

function ServiceOrder() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [serviceOrders, setServiceOrders] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [modalFilter, setModalFilter] = useState(false)
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [load, setLoad] = useState(false)

    const [myPermission, setMyPermission] = useState({ group: "" })
    const { checkPermission } = useMyPermission()
    const myPermissionNew = checkPermission(myPermission, "serviceOrder_Create")
    const myPermissionView = checkPermission(myPermission, "serviceOrder_View")

    const getAll = () => {
        api.get(`/serviceOrder/getall`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response) {
                setServiceOrders(response.data.serviceOrders)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => { getAll() }, [token])

    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return serviceOrders.filter((item) => {
            const matchSearch =
                (item.name || '').toLowerCase().includes(searchLowerCase) ||
                (item.code || '').toLowerCase().includes(searchLowerCase)
            const matchStatus = filters.status === 'all' || item.status?.value === filters.status
            const matchClient = !filters.client || item.client?._id === filters.client._id || item.client?.value === filters.client.value
            const matchTechnician = !filters.technician || (item.technicians || []).some(
                (t) => (t._id || t.value) === (filters.technician._id || filters.technician.value)
            )
            const matchProcedure = !filters.procedure || item.procedure?._id === filters.procedure._id || item.procedure?.value === filters.procedure.value
            const matchDateFrom = !filters.dateFrom || (item.scheduledDate && item.scheduledDate >= filters.dateFrom)
            const matchDateTo = !filters.dateTo || (item.scheduledDate && item.scheduledDate <= filters.dateTo)
            return matchSearch && matchStatus && matchClient && matchTechnician && matchProcedure && matchDateFrom && matchDateTo
        }).sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
    }, [serviceOrders, search, filters])

    const hasActiveFilter = !!search
        || filters.status !== 'all'
        || !!filters.client
        || !!filters.technician
        || !!filters.procedure
        || !!filters.dateFrom
        || !!filters.dateTo

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando ordens de serviço...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Ordens de Serviço</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionNew && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar ordem de serviço..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'ordem' : 'ordens'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'ordem encontrada' : 'ordens encontradas'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListServiceOrderRow
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermission={myPermission}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma ordem de serviço encontrada</p>
                        <p className="pg-empty-subtitle">
                            {serviceOrders.length === 0
                                ? 'Cadastre a primeira ordem de serviço para começar.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                w={80}
                content={
                    <NewServiceOrder
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

            {modalFilter && (
                <ServiceOrderFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar ordens de serviço</p>
        </div>
    )
}

export default ServiceOrder
